import React, { useState, useEffect, useCallback, useRef } from 'react'
import { flushSync } from 'react-dom'
import { useConnectionStore } from '@/stores/connectionStore'
import { AIAssistantService } from '@/services/aiAssistantService'
import { AgentManagementService } from '@/services/api'
import { toast } from 'react-hot-toast'
import { ChromeTabs, type ChromeTab as ChromeTabType } from '@/components/common/ChromeTabs'
import {
  PaperAirplaneIcon,
  SparklesIcon,
  XMarkIcon,
  PlayIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EyeIcon,
  CodeBracketIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'

// AI Assistant message types
interface AIMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  sqlQuery?: string
  explanation?: string
  isLoading?: boolean
  error?: string
  eventType?: string
  thinking?: string
  queryResults?: { query: string; results: unknown; executionTime?: number }[]
  agentTrace?: {
    tool?: string
    actionInput?: string
    observation?: string
  }
  agentTraces?: {
    action?: string
    actionInput?: string
    observation?: string
  }[]
  isAutonomous?: boolean
  thinkingHistory?: {
    eventType?: string
    thinking: string
    timestamp: Date
    icon?: string
    agentAction?: string
    toolName?: string
    tool?: string
    toolInputPreview?: string
    stepCount?: number
    serverTimestamp?: number
  }[]
  agentTraceHistory?: {
    tool?: string
    actionInput?: string
    observation?: string
    timestamp: Date
  }[]
  subtype?: 'steps' | 'explanation'
  durationMs?: number
}

// AI Assistant chat history storage keys
const CHAT_HISTORY_KEY = 'mazpin_ai_assistant_history_'
const CHAT_SESSIONS_KEY = 'mazpin_ai_assistant_sessions_'

// Available AI models for SQL and database work (default: GPT-4o)
const AVAILABLE_MODELS = [
  { id: 'gpt-4o', name: 'GPT-4o', description: 'Advanced model for complex SQL tasks' },
  { id: 'gpt-5', name: 'GPT-5', description: 'Latest model with enhanced reasoning for SQL generation' }
] as const

// Local storage key for selected model
const SELECTED_MODEL_KEY = 'mazpin_ai_assistant_selected_model'

// Local storage key for how many messages to remember
const REMEMBER_MESSAGES_KEY = 'mazpin_ai_assistant_remember_count'
// Local storage key for autonomous mode enabled state
const AUTONOMOUS_MODE_KEY = 'mazpin_ai_assistant_autonomous_mode'
// Local storage key for max iterations in autonomous mode
const MAX_ITERATIONS_KEY = 'mazpin_ai_assistant_max_iterations'

interface AIAssistantProps {
  isOpen: boolean
  onClose: () => void
  onExecuteQuery: (query: string) => void
  selectedDatabase?: string
  selectedSchema?: string
  connectionType?: string
}

interface ChatSession {
  id: string
  label: string
  messages: AIMessage[]
  createdAt: string
  updatedAt: string
}

interface ChatSessionsState {
  sessions: ChatSession[]
  activeSessionId: string
}

// Build a chat history prefix including previous questions and successful answers
function buildChatHistoryPrefix(history: AIMessage[], limit?: number): string {
  if (!history || history.length === 0) return ''
  // When rememberCount is 1 (or less), do not include any prior chat history
  if (typeof limit === 'number' && limit <= 1) return ''

  // Build the most recent answered Q/A pairs by scanning backward, so "1" always
  // yields the last completed pair where the assistant has an actual answer.
  interface QAPair { q: string; a?: string }
  const pairs: QAPair[] = []

  let i = history.length - 1
  const maxPairs = Math.min(typeof limit === 'number' && limit > 0 ? limit : Number.POSITIVE_INFINITY, 8)

  while (i >= 0 && pairs.length < maxPairs) {
    // Find last assistant answer with real content (prefer explanation in autonomous mode)
    let answerText: string | undefined
    let j = i
    while (j >= 0) {
      const msg = history[j]
      if (msg.role === 'assistant') {
        const hasError = typeof msg.error === 'string' && msg.error.length > 0
        const isLoading = msg.isLoading === true
        const text = (typeof msg.explanation === 'string' && msg.explanation.trim().length > 0)
          ? msg.explanation.trim()
          : (typeof msg.content === 'string' && msg.content.trim().length > 0 ? msg.content.trim() : '')
        if (!hasError && !isLoading && text) {
          answerText = text
          j--
          break
        }
      }
      j--
    }

    // Find the nearest preceding user question
    let questionText: string | undefined
    while (j >= 0) {
      const msg = history[j]
      if (msg.role === 'user') {
        const q = typeof msg.content === 'string' ? msg.content.trim() : ''
        if (q) {
          questionText = q
          j--
          break
        }
      }
      j--
    }

    if (questionText) {
      pairs.push({ q: questionText, a: answerText })
      i = j
    } else {
      // No preceding user question found; stop building further
      break
    }
  }

  // If no completed pairs found but there is at least one user message, include that question alone
  if (pairs.length === 0) {
    const lastUser = [...history].reverse().find(m => m.role === 'user' && typeof m.content === 'string' && m.content.trim().length > 0)
    if (!lastUser) return ''
    const linesSingle = [`Q: ${lastUser.content.trim()}`]
    const headerSingle = 'Chat History (use for context; mainly focus on the current question, but consider prior questions and answers as well):\n'
    return `${headerSingle}${linesSingle.join('\n')}\n\n`
  }

  // Pairs were collected newest-first; reverse to chronological order
  pairs.reverse()

  const lines: string[] = []
  for (const p of pairs) {
    lines.push(`Q: ${p.q}`)
    if (p.a) lines.push(`A: ${p.a}`)
  }

  const header = 'Chat History (use for context; mainly focus on the current question, but consider prior questions and answers as well):\n'
  return `${header}${lines.join('\n')}\n\n`
}

type StoredAIMessage = Omit<AIMessage, 'timestamp'> & { timestamp: string }
type StoredChatSession = Omit<ChatSession, 'messages'> & { messages: StoredAIMessage[] }

// Function to save chat sessions to localStorage
function saveChatSessions(datasourceId: string, state: ChatSessionsState): void {
  try {
    const key = `${CHAT_SESSIONS_KEY}${datasourceId}`
    const payload: { activeSessionId: string; sessions: StoredChatSession[] } = {
      activeSessionId: state.activeSessionId,
      sessions: state.sessions.map(s => ({
        id: s.id,
        label: s.label,
        createdAt: s.createdAt,
        updatedAt: s.updatedAt,
        messages: s.messages.slice(-50).map(m => ({
          ...m,
          timestamp: m.timestamp.toISOString()
        }))
      }))
    }
    localStorage.setItem(key, JSON.stringify(payload))
  } catch (error) {
    console.warn('Failed to save AI chat sessions:', error)
  }
}

// Function to load chat sessions from localStorage (with migration from single-history key)
function loadChatSessions(datasourceId: string): ChatSessionsState {
  try {
    const sessionsKey = `${CHAT_SESSIONS_KEY}${datasourceId}`
    const storedSessions = localStorage.getItem(sessionsKey)
    if (storedSessions) {
      const parsed = JSON.parse(storedSessions) as unknown
      if (typeof parsed === 'object' && parsed !== null && 'activeSessionId' in parsed && 'sessions' in parsed) {
        const data = parsed as {
          activeSessionId: unknown
          sessions: unknown
        }
        
        // Helper to revive various timestamp formats into Date
        const reviveToDate = (value: unknown): Date => {
          if (value instanceof Date) return value
          if (typeof value === 'number') return new Date(value)
          if (typeof value === 'string') {
            const d = new Date(value)
            return isNaN(d.getTime()) ? new Date() : d
          }
          return new Date()
        }

        const activeSessionId = typeof data.activeSessionId === 'string' ? data.activeSessionId : ''
        const sessionsArray = Array.isArray(data.sessions) ? data.sessions : []
        
        const state: ChatSessionsState = {
          activeSessionId,
          sessions: sessionsArray.map((s: unknown) => {
            if (typeof s !== 'object' || s === null) {
              return { id: '', label: '', messages: [], createdAt: '', updatedAt: '' }
            }
            const messages = 'messages' in s && Array.isArray(s.messages) ? s.messages : []
            
            return {
              id: 'id' in s && typeof s.id === 'string' ? s.id : '',
              label: 'label' in s && typeof s.label === 'string' ? s.label : '',
              createdAt: 'createdAt' in s && typeof s.createdAt === 'string' ? s.createdAt : '',
              updatedAt: 'updatedAt' in s && typeof s.updatedAt === 'string' ? s.updatedAt : '',
              messages: messages.map((m: unknown) => {
                if (typeof m !== 'object' || m === null) {
                  return { id: '', role: 'user' as const, content: '', timestamp: new Date() }
                }
                const result: AIMessage = {
                  id: 'id' in m && typeof m.id === 'string' ? m.id : '',
                  role: ('role' in m && (m.role === 'user' || m.role === 'assistant')) ? m.role : 'user' as const,
                  content: 'content' in m && typeof m.content === 'string' ? m.content : '',
                  timestamp: reviveToDate('timestamp' in m ? m.timestamp : undefined),
                }
                
                if ('thinkingHistory' in m && Array.isArray(m.thinkingHistory)) {
                  result.thinkingHistory = m.thinkingHistory.map((th: unknown) => {
                    if (typeof th !== 'object' || th === null) {
                      return {
                        thinking: '',
                        timestamp: new Date()
                      }
                    }
                    return {
                      eventType: 'eventType' in th && typeof th.eventType === 'string' ? th.eventType : undefined,
                      thinking: 'thinking' in th && typeof th.thinking === 'string' ? th.thinking : '',
                      timestamp: reviveToDate('timestamp' in th ? th.timestamp : 'serverTimestamp' in th ? th.serverTimestamp : undefined),
                      icon: 'icon' in th && typeof th.icon === 'string' ? th.icon : undefined,
                      agentAction: 'agentAction' in th && typeof th.agentAction === 'string' ? th.agentAction : undefined,
                      toolName: 'toolName' in th && typeof th.toolName === 'string' ? th.toolName : undefined,
                      tool: 'tool' in th && typeof th.tool === 'string' ? th.tool : undefined,
                      toolInputPreview: 'toolInputPreview' in th && typeof th.toolInputPreview === 'string' ? th.toolInputPreview : undefined,
                      stepCount: 'stepCount' in th && typeof th.stepCount === 'number' ? th.stepCount : undefined,
                      serverTimestamp: 'serverTimestamp' in th && typeof th.serverTimestamp === 'number' ? th.serverTimestamp : undefined,
                    }
                  })
                } else if ('thinkingHistory' in m && m.thinkingHistory !== undefined && m.thinkingHistory !== null) {
                  result.thinkingHistory = []
                }
                
                if ('agentTraceHistory' in m && Array.isArray(m.agentTraceHistory)) {
                  result.agentTraceHistory = m.agentTraceHistory.map((th: unknown) => {
                    if (typeof th !== 'object' || th === null) {
                      return {
                        timestamp: new Date()
                      }
                    }
                    return {
                      tool: 'tool' in th && typeof th.tool === 'string' ? th.tool : undefined,
                      actionInput: 'actionInput' in th && typeof th.actionInput === 'string' ? th.actionInput : undefined,
                      observation: 'observation' in th && typeof th.observation === 'string' ? th.observation : undefined,
                      timestamp: reviveToDate('timestamp' in th ? th.timestamp : undefined)
                    }
                  })
                } else if ('agentTraceHistory' in m && m.agentTraceHistory !== undefined && m.agentTraceHistory !== null) {
                  result.agentTraceHistory = []
                }
                
                return result
              })
            }
          })
        }
        
        // Validate structure
        if (!state.sessions || state.sessions.length === 0) {
          throw new Error('Empty sessions')
        }
        if (!state.sessions.find(s => s.id === state.activeSessionId)) {
          state.activeSessionId = state.sessions[0].id
        }
        return state
      }
    }

    // Migration: load old single-history key as one default session
    const historyKey = `${CHAT_HISTORY_KEY}${datasourceId}`
    const legacy = localStorage.getItem(historyKey)
    let legacyMessages: AIMessage[] = []
    if (legacy) {
      const parsed = JSON.parse(legacy) as unknown
      if (Array.isArray(parsed)) {
        legacyMessages = parsed.map((msg: unknown) => {
          if (typeof msg !== 'object' || msg === null) {
            return { id: '', role: 'user' as const, content: '', timestamp: new Date() }
          }
          const baseMsg: AIMessage = {
            id: 'id' in msg && typeof msg.id === 'string' ? msg.id : '',
            role: ('role' in msg && (msg.role === 'user' || msg.role === 'assistant')) ? msg.role : 'user' as const,
            content: 'content' in msg && typeof msg.content === 'string' ? msg.content : '',
            timestamp: new Date('timestamp' in msg && typeof msg.timestamp === 'string' ? msg.timestamp : Date.now())
          }
          // Copy over any additional properties
          if ('thinkingHistory' in msg) baseMsg.thinkingHistory = msg.thinkingHistory as AIMessage['thinkingHistory']
          if ('agentTraceHistory' in msg) baseMsg.agentTraceHistory = msg.agentTraceHistory as AIMessage['agentTraceHistory']
          if ('error' in msg) baseMsg.error = msg.error as string | undefined
          if ('sqlQuery' in msg) baseMsg.sqlQuery = msg.sqlQuery as string | undefined
          return baseMsg
        })
      }
    }
    const nowIso = new Date().toISOString()
    const defaultState: ChatSessionsState = {
      sessions: [
        {
          id: 'chat-1',
          label: 'Chat 1',
          messages: legacyMessages,
          createdAt: nowIso,
          updatedAt: nowIso
        }
      ],
      activeSessionId: 'chat-1'
    }
    return defaultState
  } catch (error) {
    console.warn('Failed to load AI chat sessions, falling back to default:', error)
    const nowIso = new Date().toISOString()
    return {
      sessions: [
        { id: 'chat-1', label: 'Chat 1', messages: [], createdAt: nowIso, updatedAt: nowIso }
      ],
      activeSessionId: 'chat-1'
    }
  }
}

export function AIAssistant({ 
  isOpen, 
  onClose, 
  onExecuteQuery, 
  selectedDatabase, 
  selectedSchema, 
  connectionType 
}: AIAssistantProps) {
  const { activeConnection } = useConnectionStore()
  // Sessions state (up to 5)
  const [sessionState, setSessionState] = useState<ChatSessionsState>(() => ({ sessions: [], activeSessionId: '' }))
  const [sessionInputs, setSessionInputs] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [hasOpenAIKey, setHasOpenAIKey] = useState(false)
  const [agentName, setAgentName] = useState<string>('')
  const [copiedMessages, setCopiedMessages] = useState<Set<string>>(new Set())
  const [selectedModel, setSelectedModel] = useState<string>(() => {
    try {
      return localStorage.getItem(SELECTED_MODEL_KEY) || 'gpt-4o'
    } catch {
      return 'gpt-4o'
    }
  })
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false)
  // LangChain is always enabled internally
  const [rememberCount, setRememberCount] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(REMEMBER_MESSAGES_KEY)
      const parsed = stored ? parseInt(stored, 10) : 5
      if (Number.isFinite(parsed)) {
        return Math.max(1, Math.min(parsed, 8))
      }
      return 5
    } catch {
      return 5
    }
  })
  const [autonomousMode, setAutonomousMode] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(AUTONOMOUS_MODE_KEY)
      // Ensure safe boolean parsing to avoid returning `any`
      return stored === 'true'
    } catch {
      return false
    }
  })
  const [maxIterations, setMaxIterations] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(MAX_ITERATIONS_KEY)
      const parsed = stored ? parseInt(stored, 10) : 10
      if (Number.isFinite(parsed)) {
        return Math.max(1, Math.min(parsed, 50))
      }
      return 10
    } catch {
      return 10
    }
  })
  
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const sessionCounterRef = useRef<number>(1)
  
  // State for expandable sections
  const [expandedSections, setExpandedSections] = useState<Record<string, Record<string, boolean>>>({})
  
  // Helper to toggle section expansion
  const toggleSection = useCallback((messageId: string, sectionKey: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [messageId]: {
        ...prev[messageId],
        [sectionKey]: !prev[messageId]?.[sectionKey]
      }
    }))
  }, [])
  
  // Load chat history when connection changes
  useEffect(() => {
    if (activeConnection) {
      const loaded = loadChatSessions(activeConnection.id)
      setSessionState(loaded)
      // Initialize inputs for sessions
      const inputs: Record<string, string> = {}
      for (const s of loaded.sessions) {
        inputs[s.id] = ''
      }
      setSessionInputs(inputs)
      // Initialize counter to next index
      const maxIndex = loaded.sessions.reduce((max, s) => {
        const match = /chat-(\d+)/.exec(s.id)
        const n = match ? parseInt(match[1] || '0', 10) : 0
        return Math.max(max, n)
      }, 0)
      sessionCounterRef.current = Math.max(1, maxIndex + 1)
    }
  }, [activeConnection])
  
  // Persist sessions when they change
  useEffect(() => {
    if (activeConnection && sessionState.sessions.length > 0) {
      saveChatSessions(activeConnection.id, sessionState)
    }
  }, [activeConnection, sessionState])
  
  // Helper getters
  const activeSession = sessionState.sessions.find(s => s.id === sessionState.activeSessionId)
  const activeInputValue = sessionInputs[sessionState.activeSessionId] || ''

  // Auto-scroll to bottom when new messages arrive in active session
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [activeSession?.messages])
  
  // Initialize expansion state for any assistant message that has thinking steps but no expansion state yet
  useEffect(() => {
    if (!activeSession?.messages) return
    setExpandedSections(prev => {
      let changed = false
      const next = { ...prev }
      for (const m of activeSession.messages) {
        if (m.role === 'assistant' && Array.isArray(m.thinkingHistory) && m.thinkingHistory.length > 0) {
          if (!next[m.id] || next[m.id].thinkingHistory === undefined) {
            next[m.id] = { ...next[m.id], thinkingHistory: false }
            changed = true
          }
        }
      }
      return changed ? next : prev
    })
  }, [activeSession?.messages])
  
  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen])
  
  // Production-ready auto-resize textarea with consistent height across screen changes
  useEffect(() => {
    const textarea = inputRef.current
    if (!textarea) return

    const minHeight = 20 // Minimum height
    const maxHeight = 80 // Maximum height
    
    const adjustHeight = () => {
      // Reset to get accurate measurements
      textarea.style.height = 'auto'
      textarea.style.overflowY = 'hidden'
      
      // Force reflow for accurate measurements
      textarea.scrollTop = 0
      const { scrollHeight } = textarea
      
      // Calculate new height
      let newHeight = Math.max(minHeight, scrollHeight)
      newHeight = Math.min(newHeight, maxHeight)
      
      // Apply new height
      textarea.style.height = `${newHeight}px`
      
      // Set overflow based on content
      if (scrollHeight > maxHeight) {
        textarea.style.overflowY = 'auto'
        // Auto-scroll to bottom when content exceeds max height
        textarea.scrollTop = textarea.scrollHeight
      } else {
        textarea.style.overflowY = 'hidden'
      }
    }

    // Handle input changes
    const handleInput = () => {
      requestAnimationFrame(() => adjustHeight())
    }
    
    // Handle resize events (window and container)
    const handleResize = () => {
      requestAnimationFrame(() => adjustHeight())
    }

    // Initial adjustment
    adjustHeight()

    // Add event listeners
    textarea.addEventListener('input', handleInput)
    window.addEventListener('resize', handleResize)
    
    // ResizeObserver for container width changes (sidebar toggles, etc.)
    let resizeObserver: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => adjustHeight())
      })
      resizeObserver.observe(textarea)
    }
    
    // Cleanup
    return () => {
      textarea.removeEventListener('input', handleInput)
      window.removeEventListener('resize', handleResize)
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [activeInputValue])
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isModelDropdownOpen && !(event.target as Element)?.closest('[data-dropdown]')) {
        setIsModelDropdownOpen(false)
      }
    }
    
    if (isModelDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isModelDropdownOpen])
  
  // Check if OpenAI API key is configured and get agent name for the current datasource
  useEffect(() => {
    const checkOpenAIKeyAndGetAgentName = async () => {
      if (!activeConnection) {
        setHasOpenAIKey(false)
        setAgentName('')
        return
      }
      
      try {
        const hasKey = await AIAssistantService.hasOpenAIKey(activeConnection.id)
        setHasOpenAIKey(hasKey)
        
        // Get agent name if agentId is available
        if (activeConnection.agentId) {
          try {
            const agent = await AgentManagementService.getAgents1({ agentId: activeConnection.agentId })
            setAgentName(agent.name || '')
          } catch (agentError) {
            console.warn('Failed to fetch agent details:', agentError)
            setAgentName('')
          }
        } else {
          setAgentName('')
        }
      } catch (error) {
        console.warn('Failed to check OpenAI API key:', error)
        setHasOpenAIKey(false)
        setAgentName('')
      }
    }
    
    void checkOpenAIKeyAndGetAgentName()
  }, [activeConnection])
  
  // Generate a unique message ID
  const generateMessageId = useCallback(() => {
    return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
  }, [])

  const formatDuration = useCallback((ms?: number) => {
    if (!ms || ms < 0) return ''
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    const remSeconds = seconds % 60
    return minutes > 0 ? `${minutes}m ${remSeconds}s` : `${remSeconds}s`
  }, [])
  
  // Handle sending a message to AI
  const handleSendMessage = useCallback(async () => {
    const currentSessionId = sessionState.activeSessionId
    const inputValue = sessionInputs[currentSessionId] || ''
    if (!inputValue.trim() || isLoading || !activeConnection || !currentSessionId) return
    
    if (!hasOpenAIKey) {
      toast.error('OpenAI API key not configured for this agent. Please configure it in the Agents page.')
      return
    }
    
    const userMessage: AIMessage = {
      id: generateMessageId(),
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    }
    
    const loadingMessage: AIMessage = {
      id: generateMessageId(),
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isLoading: true,
      isAutonomous: autonomousMode
    }
    
    // Build history prefix from existing messages (before pushing the new ones)
    const priorHistory = (activeSession?.messages || []).filter(m => !m.isLoading)
    const historyPrefix = buildChatHistoryPrefix(priorHistory, rememberCount)
    
    setSessionState(prev => {
      const updated = prev.sessions.map(s => (
        s.id === currentSessionId
          ? { ...s, messages: [...s.messages, userMessage, loadingMessage], updatedAt: new Date().toISOString() }
          : s
      ))
      return { ...prev, sessions: updated }
    })
    setSessionInputs(prev => ({ ...prev, [currentSessionId]: '' }))
    setIsLoading(true)
    
    // Add component-level timeout as fallback
    const componentTimeout = setTimeout(() => {
      console.warn('⏰ [AI Assistant UI] Component timeout reached, forcing UI completion')
      setSessionState(prev => ({
        ...prev,
        sessions: prev.sessions.map(s => s.id === currentSessionId
          ? {
              ...s,
              messages: s.messages.map(msg => msg.id === loadingMessage.id
                ? { ...msg, content: 'Request timed out. Please try again.', error: 'Component timeout', isLoading: false }
                : msg
              )
            }
          : s
        )
      }))
      setIsLoading(false)
    }, 35000) // 35 seconds (5 seconds more than service timeout)
    
    try {
      // Send message to AI Assistant service with real-time streaming
      const finalMessage = historyPrefix
        ? `${historyPrefix}Current question: ${userMessage.content}`
        : userMessage.content

      await AIAssistantService.sendMessageStream({
        datasourceId: activeConnection.id,
        message: finalMessage,
        model: selectedModel,
        useLangChain: true, // Always enabled
        autonomousMode: autonomousMode,
        maxIterations: maxIterations,
        context: {
          database: selectedDatabase,
          schema: selectedSchema,
          connectionType: connectionType,
          tables: [] // TODO: Get available tables from explorer state
        }
      }, (chunk) => {
        interface ExtraChunkFields {
          icon?: string
          agentAction?: string
          toolName?: string
          tool?: string
          toolInputPreview?: string
          stepCount?: number
          serverTimestamp?: number
        }
        const extra = chunk as unknown as ExtraChunkFields
        console.log('🔥 [AI Assistant UI] Received streaming chunk:', chunk)
        
        // Handle streaming chunks in real-time
        if (chunk.error) {
          console.error('❌ [AI Assistant UI] Chunk error:', chunk.error)
          clearTimeout(componentTimeout)
          setSessionState(prev => ({
            ...prev,
            sessions: prev.sessions.map(s => s.id === currentSessionId
              ? {
                  ...s,
                  messages: s.messages.map(msg => msg.id === loadingMessage.id
                    ? { ...msg, content: `Error: ${chunk.error}`, error: chunk.error, isLoading: false }
                    : msg
                  )
                }
              : s
            )
          }))
          setIsLoading(false)
          return
        }
        
        // Stream content character by character for real-time effect
        if (chunk.content || chunk.thinking || chunk.eventType || chunk.queryResults || chunk.agentTrace || chunk.explanation || chunk.agentTraces) {
          console.log('🎬 [AI Assistant UI] Streaming chunk data:', {
            content: chunk.content,
            explanation: chunk.explanation,
            thinking: chunk.thinking,
            eventType: chunk.eventType,
            queryResults: chunk.queryResults,
            agentTrace: chunk.agentTrace,
            agentTraces: chunk.agentTraces,
            hasThinking: !!chunk.thinking,
            thinkingLength: chunk.thinking?.length,
            isAutonomous: autonomousMode,
            done: chunk.done,
            hasExplanation: !!chunk.explanation,
            hasAgentTraces: !!chunk.agentTraces,
            agentTracesCount: chunk.agentTraces?.length || 0
          })
          
          // Use flushSync to force immediate DOM update for real-time streaming effect
          flushSync(() => {
            setSessionState(prev => ({
              ...prev,
              sessions: prev.sessions.map(s => s.id === currentSessionId
                ? {
                    ...s,
                    messages: s.messages.map(msg => msg.id === loadingMessage.id
                      ? {
                          ...msg,
                          // For autonomous mode, only accumulate content if it's not thinking/explanation data
                          content: chunk.content && !(msg.isAutonomous && (chunk.thinking || chunk.explanation)) 
                            ? (msg.content || '') + chunk.content 
                            : msg.content,
                          explanation: chunk.explanation || msg.explanation,
                          thinking: chunk.thinking || msg.thinking,
                          eventType: chunk.eventType || msg.eventType,
                          queryResults: chunk.queryResults || msg.queryResults,
                          agentTrace: chunk.agentTrace || msg.agentTrace,
                          agentTraces: chunk.agentTraces || msg.agentTraces,
                          sqlQuery: chunk.sqlQuery || msg.sqlQuery,
                          // Accumulate thinking history (prefer explicit thinking; fallback to content if provided)
                          thinkingHistory: (chunk.thinking || (chunk.eventType && (chunk.content || chunk.explanation))) ? [
                            ...(msg.thinkingHistory || []),
                            {
                              eventType: chunk.eventType,
                              thinking: chunk.thinking || chunk.content || chunk.explanation || '',
                              timestamp: new Date(),
                              icon: extra.icon,
                              agentAction: extra.agentAction,
                              toolName: extra.toolName,
                              tool: extra.tool,
                              toolInputPreview: extra.toolInputPreview,
                              stepCount: extra.stepCount,
                              serverTimestamp: extra.serverTimestamp
                            }
                          ] : msg.thinkingHistory,
                          // Accumulate agent trace history
                          agentTraceHistory: chunk.agentTrace ? [
                            ...(msg.agentTraceHistory || []),
                            {
                              ...chunk.agentTrace,
                              timestamp: new Date()
                            }
                          ] : msg.agentTraceHistory,
                          isLoading: !chunk.done
                        }
                      : msg
                    )
                  }
                : s
              )
            }))
          })
        } else if (chunk.sqlQuery && !chunk.content) {
          // Handle SQL query updates without content
          flushSync(() => {
            setSessionState(prev => ({
              ...prev,
              sessions: prev.sessions.map(s => s.id === currentSessionId
                ? {
                    ...s,
                    messages: s.messages.map(msg => msg.id === loadingMessage.id
                      ? {
                          ...msg,
                          sqlQuery: chunk.sqlQuery || msg.sqlQuery,
                          isLoading: !chunk.done
                        }
                      : msg
                    )
                  }
                : s
              )
            }))
          })
        } else if (chunk.done) {
          // Handle completion, and split autonomous responses into separate messages
          flushSync(() => {
            setSessionState(prev => ({
              ...prev,
              sessions: prev.sessions.map(s => {
                if (s.id !== currentSessionId) return s
                const msgIndex = s.messages.findIndex(m => m.id === loadingMessage.id)
                if (msgIndex === -1) return s
                const loadingMsg = s.messages[msgIndex]

                // If autonomous with steps/explanation, split into two messages like Cursor UI
                if (loadingMsg.isAutonomous) {
                  const now = new Date()
                  // Compute total duration from earliest step timestamp
                  const allTimestamps: Date[] = []
                  if (Array.isArray(loadingMsg.thinkingHistory)) {
                    for (const t of loadingMsg.thinkingHistory) allTimestamps.push(t.timestamp)
                  }
                  if (Array.isArray(loadingMsg.agentTraceHistory)) {
                    for (const t of loadingMsg.agentTraceHistory) allTimestamps.push(t.timestamp)
                  }
                  allTimestamps.push(loadingMsg.timestamp)
                  const startTs = allTimestamps.reduce((min, d) => (d && d.getTime() < min.getTime() ? d : min), loadingMsg.timestamp)
                  const durationMs = now.getTime() - startTs.getTime()

                  const stepsMessage: AIMessage = {
                    id: generateMessageId(),
                    role: 'assistant',
                    content: '',
                    timestamp: now,
                    isAutonomous: true,
                    // Prefer accumulated histories; fall back to final chunk payloads
                    thinkingHistory: loadingMsg.thinkingHistory,
                    agentTraceHistory: loadingMsg.agentTraceHistory,
                    agentTraces: (loadingMsg.agentTraces && loadingMsg.agentTraces.length > 0) ? loadingMsg.agentTraces : (chunk.agentTraces || undefined),
                    queryResults: (loadingMsg.queryResults && loadingMsg.queryResults.length > 0) ? loadingMsg.queryResults : (chunk.queryResults || undefined),
                    subtype: 'steps',
                    durationMs,
                    isLoading: false
                  }

                  const explanationText = chunk.explanation || loadingMsg.explanation || loadingMsg.content || ''
                  const answerMessage: AIMessage = {
                    id: generateMessageId(),
                    role: 'assistant',
                    content: explanationText,
                    explanation: chunk.explanation || loadingMsg.explanation,
                    timestamp: now,
                    isAutonomous: true,
                    subtype: 'explanation',
                    isLoading: false
                  }

                  const newMessages = [...s.messages]
                  // Replace loading message with steps, then insert answer after
                  newMessages.splice(msgIndex, 1, stepsMessage)
                  newMessages.splice(msgIndex + 1, 0, answerMessage)
                  // Ensure the newly added steps card is collapsed by default
                  setExpandedSections(prev => ({
                    ...prev,
                    [stepsMessage.id]: {
                      ...prev[stepsMessage.id],
                      thinkingHistory: false
                    }
                  }))
                  return { ...s, messages: newMessages }
                }

                // Default: just mark loading false
                return {
                  ...s,
                  messages: s.messages.map(m => m.id === loadingMessage.id ? { ...m, isLoading: false } : m)
                }
              })
            }))
            // Auto-expand the thinking process for the newly created steps message
            setExpandedSections(prev => ({
              ...prev,
              // Use the latest steps message id by referencing the last assistant message if needed
              // However we have access to stepsMessage in the closure above; safest is to set for all new assistant messages in active session
              // Here we optimistically set expansion for any message ids created in this tick is fine
              // We'll set both the loadingMessage.id (no longer present) and potential newly created ones by scanning active session messages on next render
              // For simplicity and reliability, set a flag for the computed id based on time is not available here, so we set after state update in a microtask
            }))
          })
        }
        
        // Always check for completion and clear global loading state
        if (chunk.done) {
          console.log('✅ [AI Assistant UI] Streaming completed')
          clearTimeout(componentTimeout)
          setIsLoading(false)
          
          // Log final message state for debugging autonomous mode
          if (autonomousMode) {
            const finalMessage = activeSession?.messages.find(m => m.id === loadingMessage.id)
            console.log('🎯 [AI Assistant UI] Final autonomous message state:', {
              thinkingHistoryLength: finalMessage?.thinkingHistory?.length || 0,
              agentTraceHistoryLength: finalMessage?.agentTraceHistory?.length || 0,
              queryResultsLength: finalMessage?.queryResults?.length || 0,
              isAutonomous: finalMessage?.isAutonomous,
              hasExplanation: !!finalMessage?.explanation,
              explanation: finalMessage?.explanation,
              hasAgentTraces: !!finalMessage?.agentTraces,
              agentTracesLength: finalMessage?.agentTraces?.length || 0,
              agentTraces: finalMessage?.agentTraces,
              allMessageData: finalMessage
            })
          }
        }
      })
      
    } catch (error) {
      clearTimeout(componentTimeout)
      console.error('Failed to get AI response:', error)
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to get AI response'
      
      setSessionState(prev => ({
        ...prev,
        sessions: prev.sessions.map(s => s.id === currentSessionId
          ? {
              ...s,
              messages: s.messages.map(msg => msg.id === loadingMessage.id
                ? {
                    ...msg,
                    content: 'Sorry, I encountered an error while processing your request. Please try again.',
                    error: errorMessage,
                    isLoading: false
                  }
                : msg
              )
            }
          : s
        )
      }))
      setIsLoading(false)
      toast.error(errorMessage)
    }
  }, [sessionState.activeSessionId, sessionInputs, isLoading, activeConnection, hasOpenAIKey, generateMessageId, selectedDatabase, selectedSchema, connectionType, selectedModel, autonomousMode, activeSession?.messages, rememberCount, maxIterations])
  
  // Handle key press in input
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleSendMessage()
    }
  }, [handleSendMessage])
  
  // Execute SQL query from AI response
  const handleExecuteSQL = useCallback((sqlQuery: string) => {
    onExecuteQuery(sqlQuery)
    // Note: Toast notifications are handled by the query execution callback
  }, [onExecuteQuery])
  
  // Clear chat history
  const handleClearHistory = useCallback(() => {
    const currentSessionId = sessionState.activeSessionId
    setSessionState(prev => ({
      ...prev,
      sessions: prev.sessions.map(s => s.id === currentSessionId ? { ...s, messages: [], updatedAt: new Date().toISOString() } : s)
    }))
  }, [sessionState.activeSessionId])
  
  // Handle model selection
  const handleModelSelect = useCallback((modelId: string) => {
    setSelectedModel(modelId)
    setIsModelDropdownOpen(false)
    try {
      localStorage.setItem(SELECTED_MODEL_KEY, modelId)
    } catch (error) {
      console.warn('Failed to save selected model to localStorage:', error)
    }
  }, [])

  // LangChain is always enabled, no toggle function needed

  // Handle autonomous mode toggle
  const handleAutonomousModeToggle = useCallback((enabled: boolean) => {
    setAutonomousMode(enabled)
    try {
      localStorage.setItem(AUTONOMOUS_MODE_KEY, JSON.stringify(enabled))
    } catch (error) {
      console.warn('Failed to save autonomous mode preference to localStorage:', error)
    }
  }, [])

  // Copy message content to clipboard
  const handleCopyMessage = useCallback(async (messageId: string, content: string) => {
    try {
      // Strip SQL code block markers for cleaner copying
      let cleanContent = content
      
      // Remove SQL code blocks (```sql ... ``` or ``` ... ```)
      const sqlCodeBlockRegex = /```(?:sql)?\s*\n?([\s\S]*?)\n?```/g
      const sqlMatch = sqlCodeBlockRegex.exec(content)
      
      if (sqlMatch && sqlMatch[1]) {
        // If there's a SQL code block, copy just the SQL content
        cleanContent = sqlMatch[1].trim()
      } else {
        // Otherwise, remove any remaining code block markers from general content
        cleanContent = content.replace(/```(?:sql)?\s*\n?/g, '').replace(/\n?```/g, '').trim()
      }
      
      await navigator.clipboard.writeText(cleanContent)
      setCopiedMessages(prev => new Set(prev).add(messageId))
      toast.success('Response copied to clipboard')
      
      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedMessages(prev => {
          const newSet = new Set(prev)
          newSet.delete(messageId)
          return newSet
        })
      }, 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
      toast.error('Failed to copy to clipboard')
    }
  }, [])
  
  // Tabs handlers
  const handleAddSession = useCallback(() => {
    setSessionState(prev => {
      if (prev.sessions.length >= 5) return prev
      const newId = `chat-${sessionCounterRef.current++}`
      const nowIso = new Date().toISOString()
      const newSession: ChatSession = { id: newId, label: `Chat ${prev.sessions.length + 1}`, messages: [], createdAt: nowIso, updatedAt: nowIso }
      return { sessions: [...prev.sessions, newSession], activeSessionId: newId }
    })
    setSessionInputs(prev => ({ ...prev }))
    // Focus textarea soon after creating
    setTimeout(() => inputRef.current?.focus(), 50)
  }, [])

  const handleCloseSession = useCallback((sessionId: string) => {
    setSessionState(prev => {
      if (prev.sessions.length <= 1) {
        // Reset to a single empty session
        const nowIso = new Date().toISOString()
        return { sessions: [{ id: 'chat-1', label: 'Chat 1', messages: [], createdAt: nowIso, updatedAt: nowIso }], activeSessionId: 'chat-1' }
      }
      const newSessions = prev.sessions.filter(s => s.id !== sessionId)
      const newActiveId = prev.activeSessionId === sessionId ? newSessions[ Math.max(0, newSessions.length - 1) ].id : prev.activeSessionId
      return { sessions: newSessions, activeSessionId: newActiveId }
    })
  }, [])

  const handleChangeSession = useCallback((sessionId: string) => {
    setSessionState(prev => ({ ...prev, activeSessionId: sessionId }))
    setTimeout(() => inputRef.current?.focus(), 0)
  }, [])

  if (!isOpen) return null
  
  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700">
        <div className="flex items-center space-x-2">
          <SparklesIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            AI Assistant
          </h3>
          {hasOpenAIKey && (
            <span className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full">
              Ready
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {(activeSession?.messages?.length || 0) > 0 && (
            <button
              type="button"
              onClick={handleClearHistory}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              title="Clear chat history"
            >
              <ArrowPathIcon className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            title="Close"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Chat tabs */}
      <ChromeTabs
        tabs={sessionState.sessions.map((s): ChromeTabType => ({ id: s.id, label: s.label, closeable: true }))}
        activeTabId={sessionState.activeSessionId}
        onTabChange={handleChangeSession}
        onTabClose={handleCloseSession}
        onTabAdd={sessionState.sessions.length < 5 ? handleAddSession : undefined}
        className="border-b border-gray-200 dark:border-gray-700"
        tabHeight={24}
        containerHeightClass="h-8"
        reserveAddButtonSpace={sessionState.sessions.length < 5}
      />
      
      {/* AI Configuration Controls */}
      {hasOpenAIKey && (
        <div className="px-2 py-1.5 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between gap-2">
            {/* Left group: Autonomous Mode and Max iterations */}
            <div className="flex items-center gap-3">
              {/* Autonomous Mode Toggle */}
              <div className="flex items-center space-x-1.5">
                <input
                  type="checkbox"
                  id="autonomous-mode-toggle"
                  checked={autonomousMode}
                  onChange={(e) => handleAutonomousModeToggle(e.target.checked)}
                  disabled={false} // LangChain is always enabled
                  className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-1 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <label 
                  htmlFor="autonomous-mode-toggle" 
                  className="text-[11px] cursor-pointer select-none text-gray-700 dark:text-gray-300"
                  title="When enabled, the AI will automatically execute generated SQL queries without manual intervention. Requires LangChain to be enabled and streaming must be active."
                >
                  Autonomous Mode
                </label>
              </div>

              {/* Max iterations (number input) - only shown when autonomous mode is enabled */}
              {autonomousMode && (
                <div className="flex items-center space-x-1.5">
                  <label
                    htmlFor="max-iterations"
                    className="text-[11px] text-gray-700 dark:text-gray-300 select-none"
                    title="Maximum number of autonomous iterations per session (1-50). Default 10."
                  >
                    Max iterations
                  </label>
                  <input
                    id="max-iterations"
                    type="number"
                    min={1}
                    max={50}
                    value={maxIterations}
                    onChange={(e) => {
                      const raw = parseInt(e.target.value, 10)
                      const clamped = Number.isFinite(raw) ? Math.max(1, Math.min(raw, 50)) : 10
                      setMaxIterations(clamped)
                      try {
                        localStorage.setItem(MAX_ITERATIONS_KEY, String(clamped))
                      } catch {
                        /* ignore localStorage issues */
                      }
                    }}
                    className="w-12 px-1 py-0.5 text-[11px] bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-700 dark:text-gray-200"
                  />
                </div>
              )}
            </div>

            {/* Right group: Remember messages and Model Selection */}
            <div className="flex items-center gap-3">
              {/* Remember messages (number input) */}
              <div className="flex items-center space-x-1.5">
                <label
                  htmlFor="remember-messages"
                  className="text-[11px] text-gray-700 dark:text-gray-300 select-none"
                  title="Chat history context size. Set to 1 to disable history; use 2–8 to include up to that many recent Q/A pairs. Default 5."
                >
                  Remember messages
                </label>
                <input
                  id="remember-messages"
                  type="number"
                  min={1}
                  max={8}
                  value={rememberCount}
                  onChange={(e) => {
                    const raw = parseInt(e.target.value, 10)
                    const clamped = Number.isFinite(raw) ? Math.max(1, Math.min(raw, 8)) : 5
                    setRememberCount(clamped)
                    try {
                      localStorage.setItem(REMEMBER_MESSAGES_KEY, String(clamped))
                    } catch {
                      /* ignore localStorage issues */
                    }
                  }}
                  className="w-12 px-1 py-0.5 text-[11px] bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-700 dark:text-gray-200"
                />
              </div>

              {/* Model Selection Dropdown */}
              <div className="relative" data-dropdown>
                <button
                type="button"
                onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
                className="flex items-center space-x-1 px-1.5 py-0.5 text-[11px] bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                title="Select AI Model"
              >
                <span className="text-gray-700 dark:text-gray-300 truncate max-w-[80px]">
                  {AVAILABLE_MODELS.find(m => m.id === selectedModel)?.name || 'GPT-4o'}
                </span>
                <ChevronDownIcon className="w-2.5 h-2.5 text-gray-500 flex-shrink-0" />
              </button>
              
              {isModelDropdownOpen && (
                <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50">
                  <div className="py-1">
                    {AVAILABLE_MODELS.map((model) => (
                      <button
                        key={model.id}
                        type="button"
                        onClick={() => handleModelSelect(model.id)}
                        className={clsx(
                          'w-full text-left px-2.5 py-1 text-[11px] hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors',
                          selectedModel === model.id
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                            : 'text-gray-700 dark:text-gray-300'
                        )}
                      >
                        <div className="font-medium">{model.name}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {!hasOpenAIKey ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-yellow-500 mb-4" />
            <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              OpenAI API Key Required
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 max-w-sm">
              To use the AI Assistant, you need to configure an OpenAI API key{agentName ? ` for the "${agentName}" agent` : ' in your agent settings'}.
            </p>
          </div>
        ) : (activeSession?.messages?.length || 0) === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <SparklesIcon className="w-12 h-12 text-blue-500 mb-4" />
            <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Welcome to AI Assistant
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 max-w-sm">
              Ask me anything about your database or request SQL queries in natural language.
            </p>
            <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
              <p>💡 Try: "Show me all users from the last month"</p>
              <p>💡 Try: "Create a query to find duplicate records"</p>
              <p>💡 Try: "Explain this table structure"</p>
              {autonomousMode && (
                <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                  <p className="text-blue-700 dark:text-blue-300 font-medium">🤖 Autonomous Mode Active</p>
                  <p className="text-blue-600 dark:text-blue-400">The AI will analyze your questions and execute queries automatically to provide comprehensive answers.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {activeSession?.messages.map((message) => (
              <div
                key={message.id}
                className={clsx(
                  'flex',
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                <div
                  className={clsx(
                    'max-w-[80%] rounded-lg p-3',
                    message.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                  )}
                >
                  {message.isLoading ? (
                    <>
                      {/* Show autonomous mode status */}
                      {message.isAutonomous && (
                        <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
                          <div className="flex items-center space-x-2 text-xs text-blue-700 dark:text-blue-300">
                            <span>🤖</span>
                            <span className="font-medium">Autonomous Mode</span>
                            {message.eventType && (
                              <>
                                <span>•</span>
                                <span className="flex items-center space-x-1">
                                  {message.eventType === 'initialization' && <span>🚀</span>}
                                  {message.eventType === 'request_analysis' && <span>🔍</span>}
                                  {message.eventType === 'thinking' && <span>💭</span>}
                                  {message.eventType === 'query_execution' && <span>⚡</span>}
                                  {message.eventType === 'analysis' && <span>📊</span>}
                                  <span className="capitalize">{message.eventType.replace('_', ' ')}</span>
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Show thinking process */}
                      {message.thinking && (
                        <div className="mb-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-md">
                          <div className="flex items-start space-x-2 text-xs text-gray-600 dark:text-gray-400">
                            <span>💭</span>
                            <span className="whitespace-pre-wrap">
                              {message.eventType && <span className="font-medium capitalize mr-1">[{message.eventType.replace('_', ' ')}]</span>}
                              {message.thinking}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* Show agent trace */}
                      {message.agentTrace && (
                        <div className="mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-md border border-yellow-200 dark:border-yellow-800">
                          <div className="text-xs text-yellow-700 dark:text-yellow-300">
                            {message.agentTrace.tool && (
                              <div className="flex items-center space-x-2 mb-1">
                                <span>🔧</span>
                                <span className="font-medium">Tool: {message.agentTrace.tool}</span>
                              </div>
                            )}
                            {message.agentTrace.actionInput && (
                              <div className="ml-4 font-mono text-xs bg-yellow-100 dark:bg-yellow-900/40 p-1 rounded">
                                {message.agentTrace.actionInput}
                              </div>
                            )}
                            {message.agentTrace.observation && (
                              <div className="ml-4 mt-1 text-xs">
                                <span className="font-medium">Result:</span> {message.agentTrace.observation}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      {message.content && (
                        <span className="inline-block w-2 h-4 bg-blue-500 animate-pulse align-middle ml-1" title="AI is typing..."></span>
                      )}
                      {!message.content && !message.thinking && !message.eventType && (
                        <div className="flex items-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                          <span className="text-sm">{message.isAutonomous ? 'Autonomous agent starting...' : 'Thinking...'}</span>
                        </div>
                      )}
                      
                      {/* Show current event type during loading */}
                      {message.isLoading && message.eventType && !message.thinking && (
                        <div className="flex items-center space-x-2">
                          {message.eventType === 'agent_decision' && <span>🤔</span>}
                          {message.eventType === 'action_input' && <span>📝</span>}
                          {message.eventType === 'planning_action' && <span>🎯</span>}
                          <span className="text-sm text-blue-600 dark:text-blue-400 font-medium capitalize">
                            {message.eventType.replace('_', ' ')}...
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Cursor-like split UI: Steps message */}
                      {message.isAutonomous && message.subtype === 'steps' && (
                        <div className="mb-3 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-gradient-to-r from-blue-50 via-green-50 to-purple-50 dark:from-blue-900/10 dark:via-green-900/10 dark:to-purple-900/10">
                          <div className="px-4 py-3 bg-white/50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center space-x-2">
                              <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs font-bold">AI</span>
                              </div>
                              <span className="font-semibold text-gray-900 dark:text-white">Plan & Steps</span>
                              {typeof message.durationMs === 'number' && (
                                <span className="ml-2 text-[11px] text-gray-600 dark:text-gray-400">{formatDuration(message.durationMs)}</span>
                              )}
                            </div>
                          </div>

                          <div className="p-4 space-y-4">
                            {message.thinkingHistory && message.thinkingHistory.length > 0 && (
                              <div className="space-y-3">
                                <button
                                  type="button"
                                  onClick={() => { toggleSection(message.id, 'thinkingHistory') }}
                                  className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg border border-purple-200 dark:border-purple-700 shadow-sm hover:from-purple-100 hover:to-indigo-100 dark:hover:from-purple-900/30 dark:hover:to-indigo-900/30 transition-colors"
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full flex items-center justify-center">
                                      <span className="text-white text-sm">💭</span>
                                    </div>
                                    <div className="text-left">
                                      <div className="font-semibold text-gray-900 dark:text-white">Thinking Process</div>
                                      <div className="text-xs text-gray-600 dark:text-gray-400">
                                        {message.thinkingHistory.length} reasoning steps
                                      </div>
                                    </div>
                                  </div>
                                  {expandedSections[message.id]?.thinkingHistory ? 
                                    <ChevronDownIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" /> : 
                                    <ChevronRightIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                  }
                                </button>

                                {expandedSections[message.id]?.thinkingHistory && (
                                  <div className="border-l-4 border-purple-200 dark:border-purple-700 pl-4 ml-4 space-y-4">
                                    {message.thinkingHistory.map((thinking, index) => (
                                      <div key={index} className="relative">
                                        {index < message.thinkingHistory!.length - 1 && (
                                          <div className="absolute left-0 top-12 w-0.5 h-8 bg-purple-200 dark:bg-purple-700"></div>
                                        )}
                                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                                          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                                            <div className="flex items-center space-x-3">
                                              <div className="w-6 h-6 bg-purple-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                                                {index + 1}
                                              </div>
                                              <div className="flex-1">
                                                {thinking.eventType && (
                                                  <div className="flex items-center space-x-2">
                                                    {thinking.icon && <span>{thinking.icon}</span>}
                                                    <span className="text-sm font-medium text-purple-700 dark:text-purple-300 capitalize">
                                                      {thinking.eventType.replace('_', ' ')}
                                                    </span>
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                                      {thinking.timestamp.toLocaleTimeString()}
                                                    </span>
                                                  </div>
                                                )}
                                                {(thinking.toolName || thinking.tool || thinking.agentAction) && (
                                                  <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                                    {thinking.agentAction && (
                                                      <span className="mr-2 capitalize">Action: {thinking.agentAction.replace('_', ' ')}</span>
                                                    )}
                                                    {thinking.toolName && (
                                                      <span className="mr-2">Tool: {thinking.toolName}</span>
                                                    )}
                                                    {!thinking.toolName && thinking.tool && (
                                                      <span className="mr-2">Tool: {thinking.tool}</span>
                                                    )}
                                                  </div>
                                                )}
                                                {!thinking.eventType && (
                                                  <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                                                    Reasoning Step {index + 1}
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                            <div className="p-4">
                                              <div className="prose prose-sm dark:prose-invert max-w-none">
                                                <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap break-words">
                                                  {thinking.thinking.split('\n').map((line, lineIndex) => (
                                                    <div key={lineIndex}>
                                                      {line.trim() || <br />}
                                                      {lineIndex < thinking.thinking.split('\n').length - 1 && <br />}
                                                    </div>
                                                  ))}
                                                </div>
                                                {thinking.toolInputPreview && (
                                                  <div className="mt-2">
                                                    <div className="text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Tool Input Preview</div>
                                                    <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                                                      <div className="text-xs font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">{thinking.toolInputPreview}</div>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}

                            {message.agentTraces && message.agentTraces.length > 0 && (
                              <div className="space-y-3">
                                <button
                                  type="button"
                                  onClick={() => { toggleSection(message.id, 'agentTraces') }}
                                  className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg border border-blue-200 dark:border-blue-700 shadow-sm hover:from-blue-100 hover:to-cyan-100 dark:hover:from-blue-900/30 dark:hover:to-cyan-900/30 transition-colors"
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                                      <span className="text-white text-sm">🔍</span>
                                    </div>
                                    <div className="text-left">
                                      <div className="font-semibold text-gray-900 dark:text-white">Execution Trace</div>
                                      <div className="text-xs text-gray-600 dark:text-gray-400">
                                        {message.agentTraces.length} execution steps
                                      </div>
                                    </div>
                                  </div>
                                  {expandedSections[message.id]?.agentTraces ? 
                                    <ChevronDownIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" /> : 
                                    <ChevronRightIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                  }
                                </button>

                                {expandedSections[message.id]?.agentTraces && (
                                  <div className="border-l-4 border-blue-200 dark:border-blue-700 pl-4 ml-4 space-y-4">
                                    {message.agentTraces.map((trace, index) => (
                                      <div key={index} className="relative">
                                        {index < message.agentTraces!.length - 1 && (
                                          <div className="absolute left-0 top-12 w-0.5 h-8 bg-blue-200 dark:bg-blue-700"></div>
                                        )}
                                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                                          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                                            <div className="flex items-center space-x-3">
                                              <div className="w-6 h-6 bg-blue-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                                                {index + 1}
                                              </div>
                                              <div className="flex items-center space-x-2">
                                                <CodeBracketIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                <span className="text-sm font-medium text-blue-700 dark:text-blue-300 capitalize">
                                                  {trace.action?.replace('_', ' ') || 'Action'}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="p-4 space-y-3">
                                            {trace.actionInput && (
                                              <div>
                                                <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">Input:</div>
                                                {(() => {
                                                  // Try to parse actionInput as JSON to extract SQL query
                                                  let parsedInput: { query?: string } | null = null
                                                  try {
                                                    const raw: unknown = JSON.parse(trace.actionInput)
                                                    if (raw && typeof raw === 'object' && !Array.isArray(raw) && 'query' in raw) {
                                                      const q = (raw as { query?: unknown }).query
                                                      parsedInput = { query: typeof q === 'string' ? q : undefined }
                                                    }
                                                  } catch {
                                                    // Not JSON, display as-is
                                                  }

                                                  const sqlQuery = parsedInput?.query
                                                  
                                                  return (
                                                    <div className="space-y-3">
                                                      {sqlQuery && (
                                                        <div>
                                                          <div className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-2 uppercase tracking-wide">SQL Query:</div>
                                                          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                                                            <div className="text-sm font-mono text-blue-800 dark:text-blue-200 leading-relaxed whitespace-pre-wrap break-words">
                                                              {sqlQuery}
                                                            </div>
                                                          </div>
                                                        </div>
                                                      )}
                                                      <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                                                        <div className="text-xs font-mono text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap break-words">
                                                          {trace.actionInput.split('\n').map((line, lineIndex) => (
                                                            <div key={lineIndex}>
                                                              {line.trim() || <br />}
                                                              {lineIndex < trace.actionInput!.split('\n').length - 1 && <br />}
                                                            </div>
                                                          ))}
                                                        </div>
                                                      </div>
                                                    </div>
                                                  )
                                                })()}
                                              </div>
                                            )}
                                            {trace.observation && (
                                              <div>
                                                <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">Result:</div>
                                                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                                  <div className="text-xs text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap break-words">
                                                    {trace.observation.split('\n').map((line, lineIndex) => (
                                                      <div key={lineIndex}>
                                                        {line.trim() || <br />}
                                                        {lineIndex < trace.observation!.split('\n').length - 1 && <br />}
                                                      </div>
                                                    ))}
                                                  </div>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}

                            {message.queryResults && message.queryResults.length > 0 && (
                              <div className="space-y-3">
                                <button
                                  type="button"
                                  onClick={() => { toggleSection(message.id, 'queryResults') }}
                                  className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg border border-amber-200 dark:border-amber-700 shadow-sm hover:from-amber-100 hover:to-orange-100 dark:hover:from-amber-900/30 dark:hover:to-orange-900/30 transition-colors"
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center">
                                      <span className="text-white text-sm">⚡</span>
                                    </div>
                                    <div className="text-left">
                                      <div className="font-semibold text-gray-900 dark:text-white">Query Results</div>
                                      <div className="text-xs text-gray-600 dark:text-gray-400">
                                        {message.queryResults.length} executed queries
                                      </div>
                                    </div>
                                  </div>
                                  {expandedSections[message.id]?.queryResults ? 
                                    <ChevronDownIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" /> : 
                                    <ChevronRightIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                  }
                                </button>

                                {expandedSections[message.id]?.queryResults && (
                                  <div className="border-l-4 border-amber-200 dark:border-amber-700 pl-4 ml-4 space-y-4">
                                    {message.queryResults.map((result, index) => {
                                      const isPreview = !expandedSections[message.id]?.[`query-${index}`]
                                      return (
                                        <div key={index} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                                          <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                                            <div className="flex items-center justify-between">
                                              <div className="flex items-center space-x-3">
                                                <div className="w-6 h-6 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                                                  {index + 1}
                                                </div>
                                                <div>
                                                  <div className="text-sm font-medium text-amber-800 dark:text-amber-200">Query {index + 1}</div>
                                                  {result.executionTime && (
                                                    <div className="text-xs text-amber-600 dark:text-amber-400">
                                                      Executed in {result.executionTime}ms
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                              <button
                                                type="button"
                                                onClick={() => { toggleSection(message.id, `query-${index}`) }}
                                                className="flex items-center space-x-1 px-2 py-1 text-xs text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30 rounded hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
                                              >
                                                <EyeIcon className="w-3 h-3" />
                                                <span>{isPreview ? 'Expand' : 'Collapse'}</span>
                                              </button>
                                            </div>
                                          </div>
                                          <div className="p-4 space-y-4">
                                            <div>
                                              <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">SQL Query:</div>
                                              <div className="p-3 bg-gray-900 dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600 overflow-x-auto">
                                                <pre className="text-xs text-green-400 dark:text-green-300 font-mono leading-relaxed whitespace-pre-wrap">
                                                  {isPreview && result.query.length > 150 ? `${result.query.substring(0, 150)}...` : result.query}
                                                </pre>
                                              </div>
                                            </div>
                                            {result.results !== null && result.results !== undefined && (
                                              <div>
                                                <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">Query Result:</div>
                                                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                                  <div className="text-xs text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap break-words font-mono">
                                                    {(() => {
                                                      const resultStr = typeof result.results === 'string' ? result.results : JSON.stringify(result.results, null, 2)
                                                      if (isPreview && resultStr.length > 300) {
                                                        return `${resultStr.substring(0, 300)}\n...(click Expand to see full result)`
                                                      }
                                                      return resultStr
                                                    })()}
                                                  </div>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Cursor-like split UI: Explanation message */}
                      {message.isAutonomous && message.subtype === 'explanation' && message.content && (
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-700 shadow-md overflow-hidden">
                          <div className="bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 px-4 py-3 border-b border-green-200 dark:border-green-700">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-sm font-bold">✓</span>
                              </div>
                              <div>
                                <div className="font-semibold text-green-800 dark:text-green-200">Final Answer</div>
                                <div className="text-xs text-green-600 dark:text-green-400">Analysis complete</div>
                              </div>
                            </div>
                          </div>
                          <div className="p-4">
                            <div className="prose prose-sm dark:prose-invert max-w-none">
                              <div className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
                                {message.content.split('\n').map((paragraph, paraIndex) => (
                                  <div key={paraIndex} className="mb-3 last:mb-0">
                                    {paragraph.trim() ? (
                                      <p className="mb-2 last:mb-0">{paragraph.trim()}</p>
                                    ) : (
                                      <br />
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Back-compat autonomous combined UI (when not split) */}
                      {(() => {
                        const shouldShowAutonomous = message.isAutonomous && !message.subtype && (message.explanation || message.agentTraces || message.queryResults)
                        return shouldShowAutonomous ? (
                          <div className="mb-3 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-gradient-to-r from-blue-50 via-green-50 to-purple-50 dark:from-blue-900/10 dark:via-green-900/10 dark:to-purple-900/10">
                            <div className="px-4 py-3 bg-white/50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                              <div className="flex items-center space-x-2">
                                <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center">
                                  <span className="text-white text-xs font-bold">AI</span>
                                </div>
                                <span className="font-semibold text-gray-900 dark:text-white">Autonomous Analysis Complete</span>
                              </div>
                            </div>
                            <div className="p-4 space-y-4">
                              {/* Thinking Process */}
                              {message.thinkingHistory && message.thinkingHistory.length > 0 && (
                                <div className="space-y-3">
                                  <button
                                    type="button"
                                    onClick={() => { toggleSection(message.id, 'thinkingHistory') }}
                                    className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg border border-purple-200 dark:border-purple-700 shadow-sm hover:from-purple-100 hover:to-indigo-100 dark:hover:from-purple-900/30 dark:hover:to-indigo-900/30 transition-colors"
                                  >
                                    <div className="flex items-center space-x-3">
                                      <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full flex items-center justify-center">
                                        <span className="text-white text-sm">💭</span>
                                      </div>
                                      <div className="text-left">
                                        <div className="font-semibold text-gray-900 dark:text-white">Thinking Process</div>
                                        <div className="text-xs text-gray-600 dark:text-gray-400">
                                          {message.thinkingHistory.length} reasoning steps
                                        </div>
                                      </div>
                                    </div>
                                    {expandedSections[message.id]?.thinkingHistory ? 
                                      <ChevronDownIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" /> : 
                                      <ChevronRightIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                    }
                                  </button>
                                  {expandedSections[message.id]?.thinkingHistory && (
                                    <div className="border-l-4 border-purple-200 dark:border-purple-700 pl-4 ml-4 space-y-4">
                                      {message.thinkingHistory.map((thinking, index) => (
                                        <div key={index} className="relative">
                                          {index < message.thinkingHistory!.length - 1 && (
                                            <div className="absolute left-0 top-12 w-0.5 h-8 bg-purple-200 dark:bg-purple-700"></div>
                                          )}
                                          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                                            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                                              <div className="flex items-center space-x-3">
                                                <div className="w-6 h-6 bg-purple-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                                                  {index + 1}
                                                </div>
                                                <div className="flex-1">
                                                  {thinking.eventType && (
                                                    <div className="flex items-center space-x-2">
                                                      {thinking.icon && <span>{thinking.icon}</span>}
                                                      <span className="text-sm font-medium text-purple-700 dark:text-purple-300 capitalize">
                                                        {thinking.eventType.replace('_', ' ')}
                                                      </span>
                                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                                        {thinking.timestamp.toLocaleTimeString()}
                                                      </span>
                                                    </div>
                                                  )}
                                                  {(thinking.toolName || thinking.tool || thinking.agentAction) && (
                                                    <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                                      {thinking.agentAction && (
                                                        <span className="mr-2 capitalize">Action: {thinking.agentAction.replace('_', ' ')}</span>
                                                      )}
                                                      {thinking.toolName && (
                                                        <span className="mr-2">Tool: {thinking.toolName}</span>
                                                      )}
                                                      {!thinking.toolName && thinking.tool && (
                                                        <span className="mr-2">Tool: {thinking.tool}</span>
                                                      )}
                                                    </div>
                                                  )}
                                                  {!thinking.eventType && (
                                                    <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                                                      Reasoning Step {index + 1}
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                            <div className="p-4">
                                              <div className="prose prose-sm dark:prose-invert max-w-none">
                                                <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap break-words">
                                                  {thinking.thinking.split('\n').map((line, lineIndex) => (
                                                    <div key={lineIndex}>
                                                      {line.trim() || <br />}
                                                      {lineIndex < thinking.thinking.split('\n').length - 1 && <br />}
                                                    </div>
                                                  ))}
                                                </div>
                                                {thinking.toolInputPreview && (
                                                  <div className="mt-2">
                                                    <div className="text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Tool Input Preview</div>
                                                    <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                                                      <div className="text-xs font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">{thinking.toolInputPreview}</div>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                              {/* Execution Trace */}
                              {(() => {
                                const traceList = message.agentTraces && message.agentTraces.length > 0
                                  ? message.agentTraces
                                  : (Array.isArray(message.agentTraceHistory) && message.agentTraceHistory.length > 0
                                      ? message.agentTraceHistory.map(h => ({
                                          action: h.tool || 'Action',
                                          actionInput: h.actionInput,
                                          observation: h.observation
                                        }))
                                      : undefined)
                                return traceList && traceList.length > 0 ? (
                                  <div className="space-y-3">
                                    <button
                                      type="button"
                                      onClick={() => { toggleSection(message.id, 'agentTraces') }}
                                      className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg border border-blue-200 dark:border-blue-700 shadow-sm hover:from-blue-100 hover:to-cyan-100 dark:hover:from-blue-900/30 dark:hover:to-cyan-900/30 transition-colors"
                                    >
                                      <div className="flex items-center space-x-3">
                                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                                          <span className="text-white text-sm">🔍</span>
                                        </div>
                                        <div className="text-left">
                                          <div className="font-semibold text-gray-900 dark:text-white">Execution Trace</div>
                                          <div className="text-xs text-gray-600 dark:text-gray-400">
                                            {traceList.length} execution steps
                                          </div>
                                        </div>
                                      </div>
                                      {expandedSections[message.id]?.agentTraces ? 
                                        <ChevronDownIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" /> : 
                                        <ChevronRightIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                      }
                                    </button>

                                    {expandedSections[message.id]?.agentTraces && (
                                      <div className="border-l-4 border-blue-200 dark:border-blue-700 pl-4 ml-4 space-y-4">
                                        {traceList.map((trace, index) => (
                                          <div key={index} className="relative">
                                            {index < traceList.length - 1 && (
                                              <div className="absolute left-0 top-12 w-0.5 h-8 bg-blue-200 dark:bg-blue-700"></div>
                                            )}
                                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                                              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                                                <div className="flex items-center space-x-3">
                                                  <div className="w-6 h-6 bg-blue-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                                                    {index + 1}
                                                  </div>
                                                  <div className="flex items-center space-x-2">
                                                    <CodeBracketIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300 capitalize">
                                                      {trace.action?.replace('_', ' ') || 'Action'}
                                                    </span>
                                                  </div>
                                                </div>
                                              </div>
                                              <div className="p-4 space-y-3">
                                                {trace.actionInput && (
                                                  <div>
                                                    <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">Input:</div>
                                                    {(() => {
                                                      // Try to parse actionInput as JSON to extract SQL query
                                                      let parsedInput: { query?: string } | null = null
                                                      try {
                                                        const raw: unknown = JSON.parse(trace.actionInput)
                                                        if (raw && typeof raw === 'object' && !Array.isArray(raw) && 'query' in raw) {
                                                          const q = (raw as { query?: unknown }).query
                                                          parsedInput = { query: typeof q === 'string' ? q : undefined }
                                                        }
                                                      } catch {
                                                        // Not JSON, display as-is
                                                      }

                                                      const sqlQuery = parsedInput?.query
                                                      
                                                      return (
                                                        <div className="space-y-3">
                                                          {sqlQuery && (
                                                            <div>
                                                              <div className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-2 uppercase tracking-wide">SQL Query:</div>
                                                              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                                                                <div className="text-sm font-mono text-blue-800 dark:text-blue-200 leading-relaxed whitespace-pre-wrap break-words">
                                                                  {sqlQuery}
                                                                </div>
                                                              </div>
                                                            </div>
                                                          )}
                                                          <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                                                            <div className="text-xs font-mono text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap break-words">
                                                              {trace.actionInput.split('\n').map((line, lineIndex) => (
                                                                <div key={lineIndex}>
                                                                  {line.trim() || <br />}
                                                                  {lineIndex < trace.actionInput!.split('\n').length - 1 && <br />}
                                                                </div>
                                                              ))}
                                                            </div>
                                                          </div>
                                                        </div>
                                                      )
                                                    })()}
                                                  </div>
                                                )}
                                                {trace.observation && (
                                                  <div>
                                                    <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">Result:</div>
                                                    <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                                      <div className="text-xs text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap break-words">
                                                        {trace.observation.split('\n').map((line, lineIndex) => (
                                                          <div key={lineIndex}>
                                                            {line.trim() || <br />}
                                                            {lineIndex < trace.observation!.split('\n').length - 1 && <br />}
                                                          </div>
                                                        ))}
                                                      </div>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ) : null
                              })()}
                              {/* Final Answer */}
                              {message.explanation && (
                                <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-700 shadow-md overflow-hidden">
                                  <div className="bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 px-4 py-3 border-b border-green-200 dark:border-green-700">
                                    <div className="flex items-center space-x-3">
                                      <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                                        <span className="text-white text-sm font-bold">✓</span>
                                      </div>
                                      <div>
                                        <div className="font-semibold text-green-800 dark:text-green-200">Final Answer</div>
                                        <div className="text-xs text-green-600 dark:text-green-400">Analysis complete</div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="p-4">
                                    <div className="prose prose-sm dark:prose-invert max-w-none">
                                      <div className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
                                        {message.explanation.split('\n').map((paragraph, paraIndex) => (
                                          <div key={paraIndex} className="mb-3 last:mb-0">
                                            {paragraph.trim() ? (
                                              <p className="mb-2 last:mb-0">{paragraph.trim()}</p>
                                            ) : (
                                              <br />
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : null
                      })()}

                      
                      {(() => {
                        // For autonomous messages, don't show raw content as it's handled by the special UI above
                        if (message.isAutonomous && !message.subtype && (message.explanation || message.agentTraces || message.queryResults)) {
                          return null
                        }
                        
                        // Filter out SQL code blocks from content if we have a separate sqlQuery
                        let displayContent = message.content
                        if (message.sqlQuery) {
                          // Remove SQL code blocks (```sql ... ``` or ``` ... ```)
                          displayContent = displayContent.replace(/```(?:sql)?\s*\n?[\s\S]*?\n?```/gm, '').trim()
                        }
                        return displayContent ? (
                          <p className="text-sm whitespace-pre-wrap">{displayContent}</p>
                        ) : null
                      })()}
                      
                      {message.sqlQuery && !message.isAutonomous && (
                        <div className="mt-3 p-3 bg-gray-800 dark:bg-gray-900 rounded-md">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-gray-400 font-medium">Generated SQL</span>
                            <button
                              type="button"
                              onClick={() => { handleExecuteSQL(message.sqlQuery!) }}
                              className="flex items-center space-x-1 px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                            >
                              <PlayIcon className="w-3 h-3" />
                              <span>Execute</span>
                            </button>
                          </div>
                          <pre className="text-xs text-gray-300 font-mono overflow-x-auto whitespace-pre-wrap break-words min-h-[80px] max-h-[200px] overflow-y-auto">
                            {message.sqlQuery}
                          </pre>
                        </div>
                      )}
                      
                      {message.error && (
                        <div className="mt-2 p-2 bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-400">
                          Error: {message.error}
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {message.timestamp.toLocaleTimeString()}
                        </span>
                        {message.role === 'assistant' && message.content && (
                          <button
                            type="button"
                            onClick={() => void handleCopyMessage(message.id, message.content)}
                            className="flex items-center space-x-1 px-1 py-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded"
                            title="Copy response to clipboard"
                          >
                            {copiedMessages.has(message.id) ? (
                              <CheckIcon className="w-3 h-3 text-green-500" />
                            ) : (
                              <ClipboardDocumentIcon className="w-3 h-3" />
                            )}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>
      
      {/* Input */}
      {hasOpenAIKey && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-end space-x-2">
            <textarea
              ref={inputRef}
              value={activeInputValue}
              onChange={(e) => {
                const { value } = e.target
                const id = sessionState.activeSessionId
                setSessionInputs(prev => ({ ...prev, [id]: value }))
              }}
              onKeyPress={handleKeyPress}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none overflow-y-hidden leading-5 break-words"
              rows={1}
              disabled={isLoading}
              style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
            />
            <button
              type="button"
              onClick={() => void handleSendMessage()}
              disabled={!activeInputValue.trim() || isLoading}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center flex-shrink-0"
              style={{ height: '36px', minHeight: '36px' }}
            >
              <PaperAirplaneIcon className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Press Enter to send, Shift+Enter for new line
          </div>
        </div>
      )}
    </div>
  )
}